// Informes (fase 5): 2 tabs — Sesiones y Alumnos.
// Auto-restricción server-side por rol (admin/teacher/inspector/company_admin).
// Filtros: rango, tipo, tracking_status, profesor/empresa/alumno (números directos por ahora;
// 5B podrá añadir autocomplete con catálogos).

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/atoms/Badge';
import { Button } from '@/components/atoms/Button';
import { Spinner } from '@/components/atoms/Spinner';
import { Header } from '@/components/organisms/Header';
import { ApiError } from '@/services/api';
import {
  downloadSessionsXlsx,
  listSessions,
  listStudents,
  type ReportFilters,
  type ReportSession,
  type ReportStudent,
} from '@/services/reports';
import styles from './Informes.module.scss';

type Tab = 'sessions' | 'students';

function firstDayOfMonth(): string {
  const d = new Date();
  d.setDate(1);
  return d.toISOString().slice(0, 10);
}
function lastDayOfMonth(): string {
  const d = new Date();
  d.setMonth(d.getMonth() + 1, 0);
  return d.toISOString().slice(0, 10);
}

const TRACKING_OPTIONS: { id: number; label: string }[] = [
  { id: 1, label: 'Present' },
  { id: 2, label: 'Absent' },
  { id: 3, label: 'In progress' },
  { id: 4, label: 'Expected' },
  { id: 5, label: 'Canceled' },
];

export function Informes() {
  const [tab, setTab] = useState<Tab>('sessions');
  const [filters, setFilters] = useState<ReportFilters>({
    from: firstDayOfMonth(),
    to: lastDayOfMonth(),
    page: 1,
    page_size: 50,
  });
  const [sessions, setSessions] = useState<ReportSession[]>([]);
  const [students, setStudents] = useState<ReportStudent[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (tab === 'sessions') {
        const r = await listSessions(filters);
        setSessions(r.items);
        setTotal(r.total);
      } else {
        const items = await listStudents(filters);
        setStudents(items);
      }
    } catch (e) {
      setError(e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Error');
    } finally {
      setLoading(false);
    }
  }, [tab, filters]);

  useEffect(() => { void reload(); }, [reload]);

  function setFilter<K extends keyof ReportFilters>(key: K, value: ReportFilters[K]) {
    setFilters((prev) => ({ ...prev, [key]: value, page: 1 }));
  }

  async function onDownload() {
    setDownloading(true);
    setError(null);
    try {
      await downloadSessionsXlsx(filters);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al descargar');
    } finally {
      setDownloading(false);
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / (filters.page_size ?? 50)));
  const page = filters.page ?? 1;

  return (
    <>
      <Header title="Informes" />
      <main className={styles.main}>
        <div className={styles.tabs}>
          <button className={`${styles.tab} ${tab === 'sessions' ? styles.tabActive : ''}`} onClick={() => setTab('sessions')}>
            Sesiones
          </button>
          <button className={`${styles.tab} ${tab === 'students' ? styles.tabActive : ''}`} onClick={() => setTab('students')}>
            Alumnos
          </button>
        </div>

        <div className={styles.filters}>
          <label className={styles.filter}>
            <span>Desde</span>
            <input type="date" value={filters.from ?? ''} onChange={(e) => setFilter('from', e.target.value || undefined)} />
          </label>
          <label className={styles.filter}>
            <span>Hasta</span>
            <input type="date" value={filters.to ?? ''} onChange={(e) => setFilter('to', e.target.value || undefined)} />
          </label>
          <label className={styles.filter}>
            <span>Tipo</span>
            <select value={filters.type ?? ''} onChange={(e) => setFilter('type', (e.target.value || undefined) as 'one' | 'gr' | undefined)}>
              <option value="">Todos</option>
              <option value="one">Individual</option>
              <option value="gr">Grupal</option>
            </select>
          </label>
          <label className={styles.filter}>
            <span>Asistencia</span>
            <select value={filters.tracking_status_id ?? ''} onChange={(e) => setFilter('tracking_status_id', e.target.value ? Number(e.target.value) : undefined)}>
              <option value="">Todas</option>
              {TRACKING_OPTIONS.map((t) => (
                <option key={t.id} value={t.id}>{t.label}</option>
              ))}
            </select>
          </label>
          <label className={styles.filter}>
            <span>Empresa (id)</span>
            <input type="number" min={1} value={filters.company_id ?? ''} onChange={(e) => setFilter('company_id', e.target.value ? Number(e.target.value) : undefined)} placeholder="—" />
          </label>
          <label className={styles.filter}>
            <span>Profesor (id)</span>
            <input type="number" min={1} value={filters.teacher_id ?? ''} onChange={(e) => setFilter('teacher_id', e.target.value ? Number(e.target.value) : undefined)} placeholder="—" />
          </label>
          <div className={styles.actions}>
            {tab === 'sessions' && (
              <Button variant="primary" onClick={() => void onDownload()} disabled={downloading || loading}>
                {downloading ? 'Generando…' : 'Descargar Excel'}
              </Button>
            )}
          </div>
        </div>

        {error && <p className={styles.error}>{error}</p>}

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}><Spinner size="lg" /></div>
        ) : tab === 'sessions' ? (
          <SessionsTable items={sessions} total={total} page={page} totalPages={totalPages} onPage={(p) => setFilter('page', p)} />
        ) : (
          <StudentsTable items={students} />
        )}
      </main>
    </>
  );
}

function SessionsTable({ items, total, page, totalPages, onPage }: { items: ReportSession[]; total: number; page: number; totalPages: number; onPage: (p: number) => void }) {
  if (items.length === 0) {
    return <div className={styles.tableWrap}><div className={styles.empty}>No hay sesiones en este rango.</div></div>;
  }
  return (
    <div className={styles.tableWrap}>
      <table>
        <thead>
          <tr>
            <th>Fecha</th><th>Hora</th><th>Tipo</th><th>Duración</th>
            <th>Profesor</th><th>Cohorte</th><th>Alumnos</th>
            <th className={styles.numeric}>Presente</th>
            <th className={styles.numeric}>Ausente</th>
            <th className={styles.numeric}>Cancelado</th>
            <th>Estado</th>
          </tr>
        </thead>
        <tbody>
          {items.map((s) => {
            const d = new Date(s.scheduled_at);
            return (
              <tr key={s.booking_id}>
                <td>{d.toLocaleDateString('es-ES')}</td>
                <td>{d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}</td>
                <td><Badge tone={s.type === 'one' ? 'accent' : 'neutral'}>{s.type === 'one' ? 'Individual' : 'Grupal'}</Badge></td>
                <td className={styles.numeric}>{s.duration_min}'</td>
                <td>{s.teacher_name}</td>
                <td>{s.cohort_name ?? '—'}</td>
                <td>{s.student_names}</td>
                <td className={styles.numeric}>{s.present_count}</td>
                <td className={styles.numeric}>{s.absent_count}</td>
                <td className={styles.numeric}>{s.canceled_count}</td>
                <td><Badge tone={s.status === 'completed' ? 'success' : s.status === 'cancelled' ? 'warning' : s.status === 'no_show' ? 'warning' : 'neutral'}>{s.status}</Badge></td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className={styles.pagination}>
        <span>{total} sesión{total === 1 ? '' : 'es'} en total</span>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button onClick={() => onPage(Math.max(1, page - 1))} disabled={page <= 1} style={{ padding: 4 }}>‹</button>
          <span>Página {page} / {totalPages}</span>
          <button onClick={() => onPage(Math.min(totalPages, page + 1))} disabled={page >= totalPages} style={{ padding: 4 }}>›</button>
        </div>
      </div>
    </div>
  );
}

function StudentsTable({ items }: { items: ReportStudent[] }) {
  if (items.length === 0) {
    return <div className={styles.tableWrap}><div className={styles.empty}>No hay alumnos con sesiones en este rango.</div></div>;
  }
  return (
    <div className={styles.tableWrap}>
      <table>
        <thead>
          <tr>
            <th>Alumno</th><th>Licencia</th><th>Nivel</th>
            <th className={styles.numeric}>Sesiones</th>
            <th className={styles.numeric}>Presentes</th>
            <th className={styles.numeric}>Ausentes</th>
            <th className={styles.numeric}>Cancel.</th>
            <th className={styles.numeric}>Minutos asistidos</th>
            <th>Detalle</th>
          </tr>
        </thead>
        <tbody>
          {items.map((s) => (
            <tr key={s.student_id}>
              <td>
                <strong>{s.display_name}</strong>
                <br/>
                <span style={{ fontSize: 11, color: '#888' }}>{s.email}</span>
              </td>
              <td><Badge tone="neutral">{s.license_slug ?? '—'}</Badge></td>
              <td>{s.level_label ?? '—'}</td>
              <td className={styles.numeric}>{s.total_sessions}</td>
              <td className={styles.numeric}>{s.present_count}</td>
              <td className={styles.numeric}>{s.absent_count}</td>
              <td className={styles.numeric}>{s.canceled_count}</td>
              <td className={styles.numeric}>{s.total_minutes_attended}'</td>
              <td><Link to={`/informes/alumno/${s.student_id}`} className={styles.studentLink}>Ver tracking →</Link></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
